import React, { useState } from 'react';
import { Phone, Mail, MapPin, ArrowRight, Paperclip } from 'lucide-react';
import { CONTACT } from '../mock';
import { toast } from '../hooks/use-toast';

export default function Contact() {
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', email: '', message: '' });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email || !form.message) {
      toast({ title: 'Uzupełnij wymagane pola', description: 'Imię, e-mail oraz wiadomość są wymagane.' });
      return;
    }
    try {
      setSending(true);
      const res = await fetch('https://formspree.io/f/moeaerop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          imie: form.firstName,
          nazwisko: form.lastName,
          telefon: form.phone,
          email: form.email,
          wiadomosc: form.message,
          _subject: `Nowe zapytanie od ${form.firstName} ${form.lastName}`.trim(),
        }),
      });
      if (res.ok) {
        toast({ title: 'Dziękuję za wiadomość', description: 'Odezwę się tak szybko, jak to możliwe.' });
        setForm({ firstName: '', lastName: '', phone: '', email: '', message: '' });
      } else {
        toast({ title: 'Nie udało się wysłać', description: 'Spróbuj ponownie lub napisz bezpośrednio na e-mail.' });
      }
    } catch (err) {
      toast({ title: 'Brak połączenia', description: 'Sprawdź internet i spróbuj ponownie.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" className="bg-white py-24 md:py-28">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="text-[12px] font-semibold tracking-[0.25em] text-[#0f1a2b]/60 mb-4">KONTAKT</div>
        <h2 className="font-heading font-extrabold text-[36px] md:text-[46px] leading-[1.05] tracking-tight text-[#0f1a2b] max-w-3xl">
          Planujesz inwestycję lub potrzebujesz wsparcia inżynierskiego?
        </h2>
        <p className="mt-5 text-[15px] text-[#0f1a2b]/70 max-w-2xl">
          Opisz zakres prac, inwestycję lub problem techniczny. Wspólnie ustalimy najlepszy sposób współpracy.
        </p>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-4">
            <a href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-4 rounded-2xl bg-[#f5f5f7] hover:bg-[#eef0f3] p-5 transition-colors">
              <div className="h-11 w-11 rounded-lg bg-[#f5c518] flex items-center justify-center flex-shrink-0">
                <Phone className="h-5 w-5 text-[#0f1a2b]" />
              </div>
              <div>
                <div className="text-[11px] font-semibold tracking-[0.2em] text-[#0f1a2b]/60">ZADZWOŃ</div>
                <div className="font-heading font-bold text-[20px] text-[#0f1a2b]">{CONTACT.phone}</div>
              </div>
            </a>

            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 rounded-2xl bg-[#f5f5f7] hover:bg-[#eef0f3] p-5 transition-colors">
              <div className="h-11 w-11 rounded-lg bg-[#f5c518] flex items-center justify-center flex-shrink-0">
                <Mail className="h-5 w-5 text-[#0f1a2b]" />
              </div>
              <div>
                <div className="text-[11px] font-semibold tracking-[0.2em] text-[#0f1a2b]/60">NAPISZ</div>
                <div className="font-heading font-bold text-[20px] text-[#0f1a2b]">{CONTACT.email}</div>
              </div>
            </a>

            <a href={CONTACT.google} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#f5c518] font-semibold text-[14px] px-3 py-2">
              <MapPin className="h-4 w-4" /> Zobacz wizytówkę Google
            </a>

            <div className="pt-4 text-[13px] text-[#0f1a2b]/65">
              <span className="font-semibold text-[#0f1a2b]">Adres:</span> Klonowa 3, 64-140 Włoszakowice.
            </div>
          </div>

          <form onSubmit={onSubmit} className="lg:col-span-7 rounded-2xl bg-white border border-slate-200 p-8 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Imię" name="firstName" value={form.firstName} onChange={onChange} required placeholder="Wpisz swoje imię" />
              <Field label="Nazwisko" name="lastName" value={form.lastName} onChange={onChange} placeholder="Wpisz swoje nazwisko" />
              <Field label="Telefon" name="phone" type="tel" value={form.phone} onChange={onChange} placeholder="np. +48 123 456 789" />
              <Field label="Adres e-mail" name="email" type="email" value={form.email} onChange={onChange} required placeholder="twoj@email.pl" />
            </div>
            <div>
              <label className="block text-[12px] font-semibold tracking-[0.15em] text-[#0f1a2b]/60 mb-2">WIADOMOŚĆ</label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                required
                rows={5}
                placeholder="Wpisz treść wiadomości..."
                className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-[14px] text-[#0f1a2b] focus:border-[#f5c518] focus:ring-2 focus:ring-[#f5c518]/30 transition-colors"
              />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2 text-[12px] text-[#0f1a2b]/60">
                <Paperclip className="h-3.5 w-3.5" />
                Dokumentację (PDF, DWG) możesz dołączyć po pierwszym kontakcie.
              </div>
              <button type="submit" disabled={sending} className="inline-flex items-center gap-2 bg-[#f5c518] hover:bg-[#eab308] disabled:opacity-70 text-[#0f1a2b] font-semibold px-6 py-3 rounded-full transition-colors">
                {sending ? 'Wysyłam...' : 'Wyślij zapytanie'} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = 'text', required, placeholder }) {
  return (
    <div>
      <label className="block text-[12px] font-semibold tracking-[0.15em] text-[#0f1a2b]/60 mb-2">{label.toUpperCase()}{required && ' *'}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-[14px] text-[#0f1a2b] placeholder:text-[#0f1a2b]/35 focus:border-[#f5c518] focus:ring-2 focus:ring-[#f5c518]/30 transition-colors"
      />
    </div>
  );
}
